import { useState, type ReactNode } from "react";
import { ChevronDown, Star } from "lucide-react";
import { agruparChips } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { useFavoritos } from "@/lib/use-favoritos";
import { EstrelaDeFavorito } from "@/components/shared/EstrelaDeFavorito";
import { COR_NEUTRA } from "./cores-do-plano";
import type { Procedimento } from "./SeletorDeProcedimento";

const ROTULO = { favoritos: "Favoritos", profissional: "Do profissional", todos: null } as const;
const titulo = "mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400";

/**
 * Atalhos de procedimento no plano: botões no desenho dos de serviço do pedido de prótese, com a
 * cor do procedimento só numa bolinha. Ordem: favoritos, os do dentista, e o resto escondido.
 * `novo` (o "Novo procedimento") fecha a última grade.
 */
export function BotoesDeProcedimento({
  procedimentos,
  selecionadoId,
  atribuidos,
  onEscolher,
  novo,
}: {
  procedimentos: Procedimento[];
  selecionadoId: string | null;
  atribuidos: string[];
  onEscolher: (p: Procedimento) => void;
  novo?: ReactNode;
}) {
  const favoritos = useFavoritos("procedimento");
  const [todos, setTodos] = useState(false);
  const { grupos, restantes } = agruparChips(procedimentos, {
    selecionados: selecionadoId ? [selecionadoId] : [],
    atribuidos,
    favoritos: favoritos.ids,
    maxVisivel: 6,
  });

  const grade = (lista: Procedimento[], ultima: boolean) => (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
      {lista.map((p) => {
        const ativo = p.id === selecionadoId;
        return (
          <div key={p.id} className="group relative">
            <button
              type="button"
              onClick={() => onEscolher(p)}
              className={cn(
                "flex min-h-8 w-full items-center gap-2 rounded-md border py-1 pl-2 pr-6 text-left text-[12.5px] font-medium leading-tight transition-colors",
                ativo
                  ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-brand-300 hover:bg-brand-50 dark:border-border dark:bg-card dark:text-foreground",
              )}
            >
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: p.color || COR_NEUTRA }} />
              <span className="min-w-0 truncate" title={p.name}>{p.name}</span>
            </button>
            <EstrelaDeFavorito
              ativo={favoritos.ids.has(p.id)}
              onAlternar={() => favoritos.alternar(p.id)}
              className="absolute right-1 top-1/2 -translate-y-1/2"
            />
          </div>
        );
      })}
      {ultima && novo}
    </div>
  );
  const restantesAbertos = restantes.length > 0 && todos;

  return (
    <div className="space-y-2.5">
      {grupos.length === 0 && grade([], true)}
      {grupos.map((g, i) => (
        <div key={g.chave}>
          {ROTULO[g.chave] && (
            <span className={titulo}>
              {g.chave === "favoritos" && <Star className="h-2.5 w-2.5 text-amber-500" fill="currentColor" />}
              {ROTULO[g.chave]}
            </span>
          )}
          {grade(g.itens, i === grupos.length - 1 && !restantesAbertos)}
        </div>
      ))}
      {restantes.length > 0 &&
        (todos ? (
          <div>
            {grupos[0]?.chave !== "todos" && <span className={titulo}>Outros procedimentos</span>}
            {grade(restantes, true)}
          </div>
        ) : (
          <button type="button" onClick={() => setTodos(true)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-zinc-800">
            <ChevronDown className="h-3 w-3" />+ {restantes.length} outro{restantes.length > 1 ? "s" : ""}
          </button>
        ))}
    </div>
  );
}
